<template>
  <div>
    <h1>{{ $t("signUpPage.confirmCode.title") }}</h1>
    <div class="message" v-if="message" v-html="message"></div>
    <div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>
    <text-field
      :label="$t('signUpPage.confirmCode.label')"
      class="input"
      type="text"
      icon="password"
      v-model="confirmCode"
      :placeholder="$t('signUpPage.confirmCode.placeholder')"
      :maxlength="6"
    />
    <div class="button-container">
      <button-default
        class="submit-button"
        @click="submitConfirmCode"
        :disabled="confirmCode.length !== 6 || submitting"
      >
        <span v-if="submitting" class="submit-button-content">
          <icon-spinner class="submit-button-spinner" />
          {{ $t("signUpPage.confirmCode.submitting") }}
        </span>
        <span v-else>{{ $t("signUpPage.confirmCode.submitButton") }}</span>
      </button-default>
    </div>
    <div class="setting">
      <a href="#" @click.prevent="resend">{{ $t("signUpPage.confirmCode.resendLink") }}</a>
      <a :href="`${help}#a9ab790429a64fae9489d2ac06671565`" target="_blank">{{
        $t("signUpPage.confirmCode.helpLink")
      }}</a>
    </div>
  </div>
</template>
<script lang="ts">
import Vue from "vue";
import TextField from "@/components/molecules/TextField.vue";
import ButtonDefault from "@/components/atoms/ButtonDefault.vue";
import { Auth } from "aws-amplify";
import IconSpinner from "~icons/figma/Spinner";

interface Props {
  email: string;
}

interface Data {
  confirmCode: string;
  message: string;
  errorMessage: string;
  submitting: boolean;
}

interface Computed {
  help: string;
}

interface Methods {
  submitConfirmCode(): Promise<void>;
  resend(): Promise<void>;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { TextField, ButtonDefault, IconSpinner },
  props: {
    email: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      confirmCode: "",
      message: "",
      errorMessage: "",
      submitting: false,
    };
  },
  computed: {
    help() {
      return process.env.VUE_APP_NOTION_HELP!;
    },
  },
  methods: {
    async submitConfirmCode() {
      if (this.submitting) return;

      const { confirmCode, email } = this;
      if (confirmCode.length !== 6 || !email) return;

      this.submitting = true;
      try {
        await Auth.confirmSignUp(email, confirmCode);

        this.$emit("complete");
        // 成功時は親側で画面遷移するため submitting は解除しない（連打抑止）
      } catch (err: any) {
        if (err.code === "CodeMismatchException") {
          this.errorMessage = this.$t("signUpPage.confirmCode.codeMismatch") as string;
        } else {
          this.errorMessage = JSON.stringify(err);
        }
        this.$emit("error");
        this.submitting = false;
      }
    },

    async resend() {
      const { email } = this;
      if (!email) return;
      try {
        await Auth.resendSignUp(email);
        this.message = this.$t("signUpPage.confirmCode.sentMessage") as string;
        this.confirmCode = "";
      } catch (err: any) {
        if (err.code === "InvalidParameterException") {
          this.errorMessage = this.$t("signUpPage.confirmCode.alreadyConfirmed") as string;
        } else if (err.code === "UserNotFoundException") {
          this.errorMessage = this.$t("signUpPage.confirmCode.userNotFound") as string;
        } else if (err.code === "LimitExceededException") {
          this.errorMessage = this.$t("signUpPage.confirmCode.limitExceeded") as string;
        } else {
          this.errorMessage = `${this.$t("signUpPage.confirmCode.genericError")} ${JSON.stringify(err)}`;
        }
      }
    },
  },
});
</script>
<style lang="scss" scoped>
h1 {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  text-align: center;
  color: var(--color-text-normal);
  margin-bottom: 12px;
}

.input {
  margin-bottom: 15px;
  --text-field-bg: var(--color-surface-base);
  --text-field-border: none;
  --text-field-icon-color: var(--color-text-low);
}

.message {
  color: var(--color-primary);
  font-weight: var(--font-weight-bold);
  margin: $margin-s 0;
  padding: 0px 15px;
}

.error-message {
  color: var(--color-warning);
  font-weight: var(--font-weight-bold);
  margin: $margin-s 0;
  padding: 0px 15px;
}

.button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;

  .submit-button {
    margin: 0;
    width: 100%;
    border-radius: var(--radius-rounded);
    background-color: var(--color-primary);
    border-color: var(--color-primary);
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-on-fixed-bg);
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }

  .submit-button-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .submit-button-spinner {
    font-size: var(--font-size-md);
    color: currentColor;
    animation: confirm-code-spinner-rotate 0.8s linear infinite;
  }
}

@keyframes confirm-code-spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}

.setting {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 10px;

  a {
    color: var(--color-primary);
  }
}
</style>
